import usePrevious from "@/shared/hooks/use-previous";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { deleteFileReq, uploadFileReq } from "@/shared/requests/upload-file";
import { addNotification } from "@/shared/states/notification";
import { useIsDemoActive } from "@/shared/states/user";
import { UploadProgress, UploadedFile } from "@/shared/types/file-upload";
import { cn } from "@/shared/utils/classname-merger";
import { Accordion, AccordionItem, Spinner } from "@nextui-org/react";
import { AxiosProgressEvent } from "axios";
import { File, RotateCw, Trash, Upload } from "lucide-react";
import React, {
  ChangeEvent,
  DragEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { ZButton } from "../../button";
import { ZProgress } from "../../progress";
import ZFileName from "./FileName";

const typeMap: { [key: string]: string } = {
  "application/json": "JSON",
  "application/msword": "Word",
  "application/pdf": "PDF",
  "application/vnd.ms-excel": "Excel",

  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "Excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
    "Excel",
  "application/vnd.ms-excel.sheet.macroEnabled.12": "Excel",
  "application/vnd.ms-excel.template.macroEnabled.12": "Excel",
  "application/vnd.ms-excel.addin.macroEnabled.12": "Excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Word",
  "image/jpeg": "JPEG",
  "image/png": "PNG",
  "text/csv": "CSV",
  // 'application/atom+xml': 'Atom',
  // 'application/javascript': 'JavaScript',
  // 'application/rss+xml': 'RSS',
  // 'application/vnd.ms-excel.sheet.binary.macroEnabled.12': 'Excel Binary',
  // 'application/vnd.ms-powerpoint': 'PowerPoint',
  // 'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  // 'application/xhtml+xml': 'XHTML',
  // 'application/xml': 'XML',
  // 'application/xml-dtd': 'DTD',
  // 'audio/midi': 'MIDI',
  // 'audio/mpeg': 'MP3',
  // 'audio/wav': 'WAV',
  // 'image/bmp': 'Bitmap',
  // 'image/gif': 'GIF',
  // 'image/svg+xml': 'SVG',
  // 'text/css': 'CSS',
  // 'text/html': 'HTML',
  // 'text/javascript': 'JavaScript',
  // 'text/markdown': 'Markdown',
  // 'text/plain': 'Plain Text',
  // 'text/xml': 'XML',
  // 'video/mp4': 'MP4',
  // 'video/quicktime': 'QuickTime',
  // 'video/webm': 'WebM',
};
export const simplifyFileTypes = (types: string[]): string[] => {

  if (types.length === 0) {
    return [...new Set(Object.keys(typeMap))];
  }

  return [...new Set(types.map((type) => typeMap[type] || type))];
};

interface FileUploadProps {
  isMultiple: boolean;
  maxFiles: number;
  renderMaxFilesValues?: number;
  allowedFileTypes: string[];
  setFiles?: (uploadFiles: UploadedFile[], type: string) => void;
  type?: string;
  orientation?: "vertical" | "horizontal";
  isShowNewFilesSection?: boolean;
  maxSizeMB?: number; // Add custom file size limit prop
  initialFiles?: UploadedFile[]; // Add this prop
  sec_ctx?: string;
  existingFileNames?: string[]; // Add prop for existing file names
  onUploadStatusChange?: (isUploading: boolean) => void; // New prop
  customDeleteHandler?: (signedUrl: string, fileId: string) => Promise<void>; // Custom delete handler
  onErrorStatusChange?: (hasErrors: boolean) => void; // Callback to notify parent about error state
  className?: string;
}

const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      isMultiple,
      maxFiles,
      renderMaxFilesValues,
      allowedFileTypes,
      setFiles,
      type,
      orientation = "vertical",
      isShowNewFilesSection = false,
      maxSizeMB = 20, // Default to 20MB
      initialFiles = [], // Add default value
      sec_ctx,
      existingFileNames = [], // Add default value
      onUploadStatusChange,
      customDeleteHandler,
      onErrorStatusChange,
      className = "",
    },
    ref
  ) => {
    // Track upload in progress state
    const [isUploading, setIsUploading] = useState(false);
    const [uploadFiles, setUploadFiles] = useState<UploadedFile[]>(initialFiles);
    const prevUploadFiles = usePrevious(uploadFiles) || [];
    const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
    const inputRef: React.RefObject<HTMLInputElement> = useRef(null);
    const isDemo = useIsDemoActive();
    
    // Add AbortController refs to track ongoing operations
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
    const uploadPromisesRef = useRef<Map<string, Promise<void>>>(new Map());

    // Cleanup function to abort all ongoing operations
    const cleanup = useCallback(() => {
      // Abort all ongoing uploads
      abortControllersRef.current.forEach((controller, fileName) => {
        if (!controller.signal.aborted) {
          controller.abort();
        }
      });
      abortControllersRef.current.clear();
      uploadPromisesRef.current.clear();
    }, []);

    // Cleanup on unmount
    useEffect(() => {
      return cleanup;
    }, [cleanup]);

    const isFileTooLarge = (file: File, megaBytes = maxSizeMB) => {
      const sizeLimit = megaBytes * 1024 * 1024;
      return file.size > sizeLimit;
    };

    useEffect(() => {
      if (setFiles && prevUploadFiles !== uploadFiles) {
        setFiles(uploadFiles, type ?? "");
      }
    }, [uploadFiles, setFiles, prevUploadFiles, type]);

    // Notify parent about error status changes
    useEffect(() => {
      if (onErrorStatusChange) {
        const hasErrors = uploadFiles.some(file =>
          ["duplicate_files", "too_large", "too_many_files", "failed", "already_exists"].includes(file.status)
        );
        onErrorStatusChange(hasErrors);
      }
    }, [uploadFiles, onErrorStatusChange]);

    const handleFiles = async (uploadedFiles: FileList) => {
      // Notify parent that upload is starting
      if (onUploadStatusChange) onUploadStatusChange(true);
      let counter = uploadFiles.length;
      let shouldShowFileTooLargeNotification = false;
      const largeFileNames: string[] = [];

      const files: UploadedFile[] = [];


      let validFilesTypes = Array.from(uploadedFiles).filter(file => allowedFileTypes.includes(file.type))

      for (const file of Array.from(validFilesTypes)) {
        const fileTooLarge = isFileTooLarge(file);
        shouldShowFileTooLargeNotification =
          shouldShowFileTooLargeNotification || fileTooLarge;

        if (fileTooLarge) {
          largeFileNames.push(file.name);
        }

        // Determine initial status based on size and count, but not duplicate yet
        // Check counter BEFORE incrementing to match original behavior
        let initialStatus: UploadedFile['status'];
        if (fileTooLarge) {
          initialStatus = "too_large";
        } else if (counter >= maxFiles) {
          initialStatus = "too_many_files";
        } else {
          initialStatus = "gettingurl";
        }

        counter = counter + 1;

        files.push({
          id: uuidv4(),
          file,
          preview: URL.createObjectURL(file),
          status: initialStatus,
          progressBar: false,
          type,
        });
      }

      // Check for duplicates AFTER all files are added with their initial status
      const fileName = new Set<string>(
        uploadFiles.map((oldFile) => oldFile.file.name)
      );
      for (const file of files) {
        if (fileName.has(file.file.name)) {
          // Mark as duplicate regardless of other status, but prioritize certain statuses
          if (file.status === "too_large") {
            // Keep too_large status as highest priority
            file.status = "too_large";
          } else {
            // Mark as duplicate - this includes files that were marked as "too_many_files"
            file.status = "duplicate_files";
          }
        } else {
          fileName.add(file.file.name);
        }

        // Check if file already exists (only if not already marked as duplicate or too_large)
        if (existingFileNames.includes(file.file.name) && file.status !== "duplicate_files" && file.status !== "too_large") {
          file.status = "already_exists";
        }
      }

      // uploadFiles.push(...files);
      const newUploadFiles = [...files];
      if (maxFiles && counter > maxFiles) {
        addNotification({
          type: "warn",
          title: "Too many files!",
          message: `You can only upload up to ${renderMaxFilesValues ?? maxFiles} for this section. Please remove ${counter - maxFiles
            } files to proceed.`,
        });
      }

      if (shouldShowFileTooLargeNotification) {
        addNotification({
          type: "error",
          title: "File size limit exceeded!",
          message:
            largeFileNames.length > 1
              ? `${largeFileNames.length} files exceed the maximum file size of ${maxSizeMB}mb. Please remove them to proceed.`
              : `The file ${largeFileNames[0]} exceeds the maximum file size of ${maxSizeMB}mb. Please remove it to proceed.`,
        });
      }

      const validFilesArray = newUploadFiles.filter(
        (fileObj: UploadedFile) => fileObj.status === "gettingurl"
      );
      const invalidFilesArray = newUploadFiles.filter(
        (fileObj: UploadedFile) => fileObj.status !== "gettingurl"
      );

      await Promise.all(
        validFilesArray.map(async (fileObj) => {
          const controller = new AbortController();
          abortControllersRef.current.set(fileObj.file.name, controller);
          
          const uploadPromise = (async () => {
            try {
              // Check if already aborted
              if (controller.signal.aborted) return;
              
              const url = await getSignedUrl({
                file_names: [fileObj.file.name],
                sec_ctx,
                ...(isDemo
                  ? {
                    max_age: 120,
                  }
                  : {}),
              });
              
              // Check if aborted after getting signed URL
              if (controller.signal.aborted) return;
              
              fileObj.signedUrl = url[0];
              fileObj.status = "inprogress";
              setUploadFiles((prevFiles) => [
                ...prevFiles,
                {
                  ...fileObj,
                  status: prevFiles.find(
                    (fil) => fil.file.name === fileObj.file.name
                  )
                    ? "duplicate_files"
                    : fileObj.status,
                },
              ]);
              setUploadProgress((prevProgress) => ({
                ...prevProgress,
                [fileObj.file.name]: 0,
              }));

              await uploadFile(fileObj, controller);
            } catch (e) {
              // Don't show error if operation was aborted
              if (!controller.signal.aborted) {
                console.error(e);
                setUploadFiles((prevFiles) => [
                  ...prevFiles,
                  { ...fileObj, status: "failed" },
                ]);
                setUploadProgress((prevProgress) => {
                  const newProgress = { ...prevProgress };
                  delete newProgress[fileObj.file.name];
                  return newProgress;
                });
              }
            } finally {
              // Clean up controller reference
              abortControllersRef.current.delete(fileObj.file.name);
              uploadPromisesRef.current.delete(fileObj.file.name);
            }
          })();
          
          uploadPromisesRef.current.set(fileObj.file.name, uploadPromise);
          return uploadPromise;
        })
      );
  setUploadFiles((prev) => [...prev, ...invalidFilesArray]);
  // Notify parent that upload is done
  if (onUploadStatusChange) onUploadStatusChange(false);
    };

    const handleRetry = async (fileObj: UploadedFile) => {
      if (onUploadStatusChange) onUploadStatusChange(true);
      // Cancel any existing upload for this file
      const existingController = abortControllersRef.current.get(fileObj.file.name);
      if (existingController) {
        existingController.abort();
      }
      
      const controller = new AbortController();
      abortControllersRef.current.set(fileObj.file.name, controller);
      
      const retryPromise = (async () => {
        try {
          fileObj.status = "retrying";
          setUploadFiles((prevFiles) => [
            ...prevFiles.map((f) => (f.id === fileObj.id ? { ...fileObj } : f)),
          ]);
          
          if (controller.signal.aborted) return;
          
          const url = await getSignedUrl({
            file_names: [fileObj.file.name],
          });
          
          if (controller.signal.aborted) return;
          
          fileObj.signedUrl = url[0];
          fileObj.status = "inprogress";
          setUploadFiles((prevFiles) => [
            ...prevFiles.map((f) => (f.id === fileObj.id ? { ...fileObj } : f)),
          ]);
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [fileObj.file.name]: 0,
          }));

          await uploadFile(fileObj, controller);
        } catch (e) {
          if (!controller.signal.aborted) {
            console.error(e);
            setUploadFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.id === fileObj.id ? { ...fileObj, status: "failed" } : f
              )
            );
            addNotification({
              message: "Error in uploading the file",
              type: "error",
            });
            setUploadProgress((prevProgress) => {
              const newProgress = { ...prevProgress };
              delete newProgress[fileObj.file.name];
              return newProgress;
            });
          }
        } finally {
          abortControllersRef.current.delete(fileObj.file.name);
          uploadPromisesRef.current.delete(fileObj.file.name);
        }
      })();
      
      uploadPromisesRef.current.set(fileObj.file.name, retryPromise);
  if (onUploadStatusChange) onUploadStatusChange(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      document.getElementById("drop-area")?.classList.remove("bg-[#F9FAFB]");

      const { files } = e.dataTransfer;
      void handleFiles(files);
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files) {
        void handleFiles(files);
        e.target.value = "";
      }
    };

    const uploadFile = async (fileObj: UploadedFile, controller?: AbortController) => {
      const name = fileObj.file.name;
      if (!fileObj.signedUrl) {
        return;
      }
      
      // Use provided controller or create a new one
      const abortController = controller || abortControllersRef.current.get(name);
      if (!abortController) return;
      
      try {
        await uploadFileReq(
          fileObj.signedUrl,
          fileObj.file,
          (progressEvent: AxiosProgressEvent) => {
            // Don't update progress if aborted
            if (abortController.signal.aborted) return;
            
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress((prevProgress) => ({
                ...prevProgress,
                [name]: progress,
              }));
            }
          },
          abortController.signal // Pass abort signal to upload request
        );
        
        // Don't update state if aborted
        if (abortController.signal.aborted) return;
        
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [name]: -1,
        }));
        setUploadFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.id === fileObj.id
              ? {
                ...f,
                status:
                  f.status === "duplicate_files"
                    ? "duplicate_files"
                    : "success",
              }
              : f
          )
        );
      } catch (error) {
        // Don't handle error if operation was aborted
        if (abortController.signal.aborted) return;
        
        console.error(error);
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [name]: 100,
        }));

        setTimeout(() => {
          if (!abortController.signal.aborted) {
            setUploadFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file.name === name ? { ...f, status: "failed" } : f
              )
            );
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [name]: -1,
            }));
          }
        }, 1000);
      }
    };

    const removeFile = (fileId: string) => {
      const fileToRemove = uploadFiles.find((f) => f.id === fileId);
      if (!fileToRemove) return;

      // Cancel ongoing upload for this file
      const controller = abortControllersRef.current.get(fileToRemove.file.name);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(fileToRemove.file.name);
        uploadPromisesRef.current.delete(fileToRemove.file.name);
      }

      setUploadFiles((prevFiles) => {
        const filteredFiles = prevFiles.filter(({ id }) => id !== fileId);
        const convertedFileNames = new Set<string>();

        let validCount = filteredFiles.filter(f =>
          !["too_many_files", "too_large", "duplicate_files", "failed", "already_exists"].includes(f.status)
        ).length;

        // First pass: promote duplicate files if they're now unique, and handle too_many_files
        const filesNeedingSignedUrl: UploadedFile[] = [];
        const firstPassFiles = filteredFiles.map(file => {
          let status = file.status;

          if (isFileTooLarge(file.file)) {
            status = "too_large";
          } else if (
            file.status === "duplicate_files" &&
            fileToRemove.file.name === file.file.name &&
            !convertedFileNames.has(file.file.name)
          ) {
            // Only promote to success if this is the ONLY file left with this name
            const filesWithSameName = filteredFiles.filter(f => f.file.name === file.file.name);
            if (filesWithSameName.length === 1) {
              status = "success";
              convertedFileNames.add(file.file.name);
              validCount++;
            }
          } else if (
            file.status === "too_many_files" &&
            validCount < maxFiles
          ) {
            // Check if this file is a duplicate before promoting to success
            const filesWithSameName = filteredFiles.filter(f => f.file.name === file.file.name);
            if (filesWithSameName.length === 1) {
              // Only one file with this name, safe to promote
              status = "success";
              validCount++;
            } else {
              // Multiple files with same name exist, keep as too_many_files for now
              // Will be re-evaluated in second pass
              status = "too_many_files";
            }
          }

          return { ...file, status };
        });

        // Second pass: rebuild fileNameSet and mark duplicates properly
        const fileNameSet = new Set<string>();
        const promotedDuplicates = new Set<string>(); // Track which duplicate filenames we've promoted

        const updatedFiles = firstPassFiles.map(file => {
          let status = file.status;

          // Build the set based on first occurrence
          if (fileNameSet.has(file.file.name)) {
            // This filename already exists - it's a duplicate
            if (status !== "too_large" && status !== "success") {
              status = "duplicate_files";
            }
          } else {
            // First occurrence of this filename
            fileNameSet.add(file.file.name);

            // If this was a duplicate file and the removed file had the same name,
            // promote the first duplicate to success
            if (
              file.status === "duplicate_files" &&
              fileToRemove.file.name === file.file.name &&
              !promotedDuplicates.has(file.file.name) &&
              validCount < maxFiles
            ) {
              status = "success";
              promotedDuplicates.add(file.file.name);
              validCount++;
            }
          }

          // If now valid and missing signedUrl, mark for upload
          if (
            status === "success" &&
            !file.signedUrl &&
            !["too_large", "too_many_files", "duplicate_files", "failed", "already_exists"].includes(status)
          ) {
            status = "gettingurl";
            filesNeedingSignedUrl.push({ ...file, status });
          }

          return { ...file, status };
        });

        // After state update, trigger upload for files needing signed URL
        setTimeout(() => {
          filesNeedingSignedUrl.forEach(async (fileObj) => {
            // You may want to deduplicate this logic into a helper
            const controller = new AbortController();
            abortControllersRef.current.set(fileObj.file.name, controller);
            const uploadPromise = (async () => {
              try {
                if (controller.signal.aborted) return;
                const url = await getSignedUrl({
                  file_names: [fileObj.file.name],
                  sec_ctx,
                  ...(isDemo ? { max_age: 120 } : {}),
                });
                if (controller.signal.aborted) return;
                fileObj.signedUrl = url[0];
                fileObj.status = "inprogress";
                setUploadFiles((prev) =>
                  prev.map((f) =>
                    f.id === fileObj.id ? { ...fileObj } : f
                  )
                );
                setUploadProgress((prev) => ({
                  ...prev,
                  [fileObj.file.name]: 0,
                }));
                await uploadFile(fileObj, controller);
              } catch (e) {
                if (!controller.signal.aborted) {
                  setUploadFiles((prev) =>
                    prev.map((f) =>
                      f.id === fileObj.id
                        ? { ...fileObj, status: "failed" }
                        : f
                    )
                  );
                  setUploadProgress((prev) => {
                    const newProgress = { ...prev };
                    delete newProgress[fileObj.file.name];
                    return newProgress;
                  });
                }
              } finally {
                abortControllersRef.current.delete(fileObj.file.name);
                uploadPromisesRef.current.delete(fileObj.file.name);
              }
            })();
            uploadPromisesRef.current.set(fileObj.file.name, uploadPromise);
          });
        }, 0);

        return updatedFiles;
      });

      if (fileToRemove.signedUrl) {
        if (customDeleteHandler) {
          // Use custom delete handler if provided
          void customDeleteHandler(fileToRemove.signedUrl, fileToRemove.id).catch(e => {
            console.error('Custom delete handler error:', e);
          });
        } else {
          // Use default blob delete handler
          void deleteFileReq(fileToRemove.signedUrl).catch(e => {
            if(e.response.statusText !== '"The specified blob does not exist."' && uploadProgress[fileToRemove.file.name] === 100 ){
              throw e
            }else {
              console.error(e)
            }
          });
        }
      }

      setUploadProgress((prevProgress) => {
        const newProgress = { ...prevProgress };
        delete newProgress[fileToRemove.file.name];
        return newProgress;
      });
    };

    const handleDropFile = () => {
      if (inputRef.current) {
        inputRef?.current.click();
      }
    };

    const doesFileHasError = useCallback((fileObj: UploadedFile) => {
      return (
        fileObj.status === "failed" ||
        fileObj.status === "too_large" ||
        fileObj.status === "too_many_files" ||
        fileObj.status === "duplicate_files" ||
        fileObj.status === "already_exists"
      );
    }, []);

    const getFileNameSuffix = useCallback((fileObj: UploadedFile) => {
      let fileNameSuffix = "";

      switch (fileObj.status) {
        case "too_large":
          fileNameSuffix = " (too large)";
          break;
        case "too_many_files":
          fileNameSuffix = " (Excess file. Remove any.)";
          break;
        case "duplicate_files":
          fileNameSuffix = " (duplicate file)";
          break;
        case "already_exists":
          fileNameSuffix = " (file already exists)";
          break;
        default:
          break;
      }

      return fileNameSuffix;
    }, []);

    const simplifiedFileTypes = simplifyFileTypes(allowedFileTypes);

    const errorFiles = (uploadFiles.filter(file => ["duplicate_files", "too_large", "too_many_files", "failed", "already_exists"].includes(file.status)))
    const validFiles = (uploadFiles.filter(file => !["duplicate_files", "too_large", "too_many_files", "failed", "already_exists"].includes(file.status)))

    const files = [...errorFiles, ...validFiles].map((fileObj: UploadedFile) => {
      return (
        <div key={fileObj.id}>
          <div className="flex items-center justify-between gap-2 mt-0">
            <File
              size={16}
              className="ml-3 min-w-4"
              color={
                fileObj.status === "success"
                  ? "#22c55e"
                  : fileObj.status === "inprogress" ? "#000" : "#e7000b"
              }
            />
            <div className="flex-1 flex flex-col justify-center ">
              <div className="flex items-center gap-1">
                <p
                  className={cn(
                    "text-sm cursor-pointer hover:underline max-w-full",
                    fileObj.status === "success"
                      ? "text-green-600"
                      : doesFileHasError(fileObj)
                        ? "text-red-600"
                        : "text-black"
                  )}
                >
                  <ZFileName filename={fileObj.file.name} />
                </p>
                <p className={cn(
                  "text-sm cursor-pointer hover:underline max-w-full whitespace-nowrap",
                  fileObj.status === "success"
                    ? "text-green-600"
                    : doesFileHasError(fileObj)
                      ? "text-red-600"
                      : "text-black"
                )}>
                  {getFileNameSuffix(fileObj)}
                </p>
                {fileObj.status === "gettingurl" ? <Spinner size="sm" /> : null}
              </div>
              <div>
                {fileObj.status !== "success" &&
                  !isNaN(uploadProgress[fileObj.file.name]) &&
                  uploadProgress[fileObj.file.name] != -1 ? (
                  <ZProgress
                    aria-label="file-upload-progress"
                    value={uploadProgress[fileObj.file.name] || 0}
                    maxValue={100}
                    className={cn("h-[3px] mt-1 [&>*]:" + "bg-blue-600")}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
            {fileObj.status === "failed" || fileObj.status === "retrying" ? (
              <ZButton
                isIconOnly
                className="bg-transparent"
                onClick={() => void handleRetry(fileObj)}
                isLoading={fileObj.status === "retrying"}
              >
                <RotateCw size={16} color="gray" />
              </ZButton>
            ) : null}
            <ZButton
              isIconOnly
              className="bg-transparent"
              onClick={() => removeFile(fileObj.id)}
            >
              <Trash size={16} color="gray" />
            </ZButton>
          </div>
        </div>
      );
    });

    const totalAllowedFiles = renderMaxFilesValues ?? maxFiles ?? 30;

    const remainingFilesUploadLimit = totalAllowedFiles - validFiles.length; 

    // Add method to cancel all uploads (can be called from parent)
    const cancelAllUploads = useCallback(() => {
      cleanup();
      // Reset upload progress
      setUploadProgress({});
      // Update file statuses to cancelled
      setUploadFiles((prevFiles) =>
        prevFiles.map((file) => ({
          ...file,
          status: file.status === "inprogress" || file.status === "gettingurl" 
            ? "failed" 
            : file.status,
        }))
      );
    }, [cleanup]);

    // Create internal ref for the DOM element
    const divRef = useRef<HTMLDivElement>(null);

    // Expose both the DOM element and cancel method via ref
    React.useImperativeHandle(ref, () => ({
      ...divRef.current,
      cancelAllUploads,
      scrollIntoView: (options?: ScrollIntoViewOptions) => {
        divRef.current?.scrollIntoView(options);
      },
    } as HTMLDivElement & { cancelAllUploads: () => void }), [cancelAllUploads]);

    return (
      <div
        ref={divRef}
        className={cn(orientation === "horizontal" && "flex gap-8")}
      >
        <div
          onDrop={handleDrop}
          id="drop-area"
          onClick={handleDropFile}
          onDragEnter={() => {
            document.getElementById("drop-area")?.classList.add("bg-[#F9FAFB]");
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          className={cn(
            "bg-[#F7F7FA] p-2 flex items-center flex-col justify-center h-32 w-full border-dashed border-[1px] border-gray-300 rounded-md cursor-pointer hover:bg-gray-50",
            orientation === "horizontal" && "w-[calc(50%-10px)]", className
          )}
        >
          <input
            type="file"
            ref={inputRef}
            multiple={isMultiple}
            onChange={handleFileChange}
            className="hidden"
            id="file-input"
            accept={allowedFileTypes.join(",")}
          />

          <Upload size={24} />

          <label className="cursor-pointer text-sm mt-4">
            Drag and drop files here or{" "}
            <span className="text-[#006FEE]">browse your computer</span>
          </label>
          <p className="text-xs text-center text-gray-500 mt-2 max-w-80">
            {`(Accepted files: ${maxFiles && maxFiles > 1 ? "Up to" : "Only"} 
            ${remainingFilesUploadLimit > 0 ? remainingFilesUploadLimit : 0} 
            file${maxFiles && maxFiles > 1 ? "s" : ""
              } of ${simplifiedFileTypes.join(", ")} of up to ${maxSizeMB}mb)`}
          </p>
        </div>
        <div
          className={cn(
            orientation === "horizontal" && "w-[calc(50%-10px)]",
            orientation === "vertical" && "w-full max-h-[200px]",
            ""
          )}
        >
          {isShowNewFilesSection && files.length > 0 ? (
            <Accordion defaultExpandedKeys={["newFiles"]}>
              <AccordionItem
                key="newFiles"
                aria-label="new files"
                title={
                  <span className="text-[14px] font-normal text-zinc-900">
                    Newly Added Files
                  </span>
                }
                className="text-[14px] font-normal file-category-heading mt-4"
              >
                <div className="max-h-[120px] overflow-y-auto">{files}</div>
              </AccordionItem>
            </Accordion>
          ) : (
            <div className="max-h-[200px] overflow-y-auto">{files}</div>
          )}
        </div>
      </div>
    );
  }
);

FileUpload.displayName = "FileUpload";

export default FileUpload;
